<script setup>
import { computed, watch, ref, nextTick, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import DeleteDialog from 'dashboard/components-next/assistant/pageComponents/DeleteDialog.vue';
import PageLayout from 'dashboard/components-next/assistant/PageLayout.vue';
import ConnectInboxDialog from 'dashboard/components-next/assistant/pageComponents/inbox/ConnectInboxDialog.vue';
import InboxCard from 'dashboard/components-next/assistant/shared/InboxCard.vue';
import InboxPageEmptyState from 'dashboard/components-next/assistant/shared/emptyStates/InboxPageEmptyState.vue';

const store = useStore();
const dialogType = ref('');
const route = useRoute();

const agentId = computed(() => Number(route.params.agentId));
const uiFlags = useMapGetter('assistantInboxes/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);

const connectedInboxes = useMapGetter('assistantInboxes/getRecords');

const selectedInbox = ref(null);
const disconnectInboxDialog = ref(null);

const handleDelete = () => {
  disconnectInboxDialog.value.dialogRef.open();
};

const connectInboxDialog = ref(null);

const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => connectInboxDialog.value.dialogRef.open());
};

const handleAction = ({ action, id }) => {
  selectedInbox.value = connectedInboxes.value.find(
    record => id === record.inbox?.id
  );
  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    }
  });
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedInbox.value = null;
};

watch(
  agentId,
  newId => {
    store.dispatch('assistantInboxes/get', {
      agentId: newId,
    });
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('assistantAgents/get');
  store.dispatch('inboxes/get');
});
</script>

<template>
  <PageLayout
    :header-title="$t('ASSISTANT.INBOXES.HEADER')"
    :button-label="$t('ASSISTANT.INBOXES.ADD_NEW')"
    :button-policy="['administrator']"
    :is-fetching="isFetching"
    :is-empty="!connectedInboxes.length"
    :show-pagination-footer="false"
    :feature-flag="FEATURE_FLAGS.AI_ASSISTANT"
    show-agent-switcher
    @click="handleCreate"
  >
    <template #emptyState>
      <InboxPageEmptyState @click="handleCreate" />
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <InboxCard
          v-for="record in connectedInboxes"
          :id="record.inbox.id"
          :key="record.id"
          :inbox="record.inbox"
          @action="handleAction"
        />
      </div>
    </template>

    <DeleteDialog
      v-if="selectedInbox"
      ref="disconnectInboxDialog"
      :entity="selectedInbox"
      :delete-payload="{
        agentId: agentId,
        inboxId: selectedInbox.inbox.id,
      }"
      type="Inboxes"
    />

    <ConnectInboxDialog
      v-if="dialogType"
      ref="connectInboxDialog"
      :agent-id="agentId"
      @close="handleCreateClose"
    />
  </PageLayout>
</template>
